import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AllProjects, BlockedView, WeeklyView } from '../components/projects/Views.jsx'
import { NewProjectDialog } from '../components/projects/NewProjectDialog.jsx'
import { ProjectView } from '../components/projects/ProjectView.jsx'
import styles from '../components/projects/projects.module.css'
import { ProjectsSidebar } from '../components/sidebar/ProjectsSidebar.jsx'
import { Dialog } from '../components/ui/Dialog.jsx'
import { LoadingState } from '../components/ui/LoadingState.jsx'
import { RenameDialog } from '../components/ui/RenameDialog.jsx'
import { useCanEditProjects, useProjectApi, useProjects } from '../lib/queries/projects.js'
import p from './pages.module.css'

/**
 * Project Management: sidebar of projects (grouped by status) and a workspace.
 * The URL holds the view: /projects, /projects?view=weekly|blocked,
 * /projects?p=<id>&tab=overview|systems|calls|sybill.
 */
export function ProjectsPage() {
  const projects = useProjects()
  const api = useProjectApi()
  const canEdit = useCanEditProjects()
  const navigate = useNavigate()
  const { p: projectId, tab, view } = useSearch({ strict: false })

  const [creating, setCreating] = useState(false)
  const [renaming, setRenaming] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const list = projects.data ?? []
  const project = projectId ? list.find((x) => x.id === projectId) : null

  // ⌘K / Ctrl+K → new project.
  useEffect(() => {
    if (!canEdit) return undefined
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setCreating(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canEdit])

  const openProject = (id, nextTab) => navigate({ to: '/projects', search: { p: id, tab: nextTab } })

  async function create(body) {
    const created = await api.create(body)
    if (created) {
      setCreating(false)
      openProject(created.id)
    }
  }

  let content
  if (projects.isPending) content = <LoadingState label="Loading projects" />
  else if (projects.isError) {
    content = (
      <div className={styles.empty}>
        Couldn't load projects.{' '}
        <button type="button" className={styles.btn} onClick={() => projects.refetch()}>
          Retry
        </button>
      </div>
    )
  } else if (projectId) {
    content = project ? (
      <ProjectView project={project} tab={tab} onTab={(t) => openProject(project.id, t)} api={api} canEdit={canEdit} />
    ) : (
      <div className={styles.empty}>That project no longer exists.</div>
    )
  } else if (view === 'weekly') content = <WeeklyView projects={list} />
  else if (view === 'blocked') content = <BlockedView projects={list} />
  else content = <AllProjects projects={list} canEdit={canEdit} onNew={() => setCreating(true)} />

  return (
    <div className={p.shell}>
      <ProjectsSidebar
        projects={projects}
        activeId={project?.id ?? null}
        view={projectId ? null : view}
        canEdit={canEdit}
        onNew={() => setCreating(true)}
        onRename={setRenaming}
        onSetStatus={(x, status) => api.patch(x.id, { status }, `Moved to ${status.replace('_', ' ')}`)}
        onDelete={setDeleting}
      />

      <main className={p.main}>
        <div className={styles.canvas}>
          <div className={styles.canvasIn}>{content}</div>
        </div>
      </main>

      <NewProjectDialog open={creating} onClose={() => setCreating(false)} onCreate={create} busy={api.saving} />
      <RenameDialog
        open={Boolean(renaming)}
        title="Rename project"
        initialTitle={renaming?.name ?? ''}
        onClose={() => setRenaming(null)}
        onSave={(name) => {
          if (name.length >= 2 && name !== renaming.name) api.patch(renaming.id, { name })
          setRenaming(null)
        }}
      />
      <Dialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Delete this project?"
        description="Its systems, credential checklists and call records are deleted with it. This can't be undone."
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={async () => {
                const id = deleting.id
                setDeleting(null)
                await api.remove(id)
                if (id === projectId) navigate({ to: '/projects' })
              }}
            >
              Delete project
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>{deleting?.name}</p>
      </Dialog>
    </div>
  )
}
